import { morph } from '@theme/morph';

/**
 * ero product page helpers.
 *
 * <ero-product-options>: flavour tiles and size cards are plain links. A click loads the target page in
 * the background and morphs <main> in place (the same approach Horizon uses for combined listings),
 * then updates the address bar: a new history entry for a flavour, a replaced one for a size.
 * Back/forward restore the matching product the same way. Any failure falls back to a normal visit.
 *
 * <ero-purchase-options>: switches between "Subscribe & save" and "One-time purchase" and keeps the
 * hidden selling_plan input (linked to the buy buttons form) in sync.
 */

/** @type {AbortController | undefined} */
let swapController;

/**
 * Loads a product URL and morphs the page's main content with it.
 * @param {string} href
 * @param {'push' | 'replace' | 'none'} historyMode
 * @param {string} [focusKey] - data-ero-key of the option to keep focused after the swap
 */
async function swapTo(href, historyMode, focusKey) {
  swapController?.abort();
  const controller = new AbortController();
  swapController = controller;

  const options = document.querySelector('ero-product-options');
  options?.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(href, { signal: controller.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    if (controller.signal.aborted) return;

    const main = document.querySelector('main');
    const newMain = html.querySelector('main');
    if (!main || !newMain) throw new Error('No main content to swap');

    const hadFocus = document.activeElement instanceof HTMLElement && main.contains(document.activeElement);
    const purchase = readPurchaseChoice();
    morph(main, newMain);
    restorePurchaseChoice(purchase);

    if (html.title) document.title = html.title;
    if (historyMode === 'push') history.pushState({ ero: true }, '', href);
    if (historyMode === 'replace') history.replaceState({ ero: true }, '', href);

    if (hadFocus && focusKey) {
      const target = document.querySelector(`[data-ero-key="${CSS.escape(focusKey)}"]`);
      if (target instanceof HTMLElement && document.activeElement !== target) target.focus({ preventScroll: true });
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
    window.location.href = href;
  } finally {
    if (swapController === controller) {
      swapController = undefined;
      document.querySelector('ero-product-options')?.removeAttribute('aria-busy');
    }
  }
}

/** Keeps "Subscribe" vs "One-time" and the delivery rhythm when the shopper changes flavour or size. */
function readPurchaseChoice() {
  const choice = document.querySelector('.ero-purchase__radio:checked');
  const plans = document.querySelector('.ero-purchase__plans');
  return {
    choice: choice instanceof HTMLInputElement ? choice.value : '',
    plan: plans instanceof HTMLSelectElement ? plans.value : '',
  };
}

/** @param {{ choice: string, plan: string }} saved */
function restorePurchaseChoice({ choice, plan }) {
  const widget = document.querySelector('ero-purchase-options');
  if (!widget) return;

  const radio = widget.querySelector(`.ero-purchase__radio[value="${CSS.escape(choice)}"]`);
  if (radio instanceof HTMLInputElement) radio.checked = true;

  const plans = widget.querySelector('.ero-purchase__plans');
  if (plans instanceof HTMLSelectElement && [...plans.options].some((option) => option.value === plan)) {
    plans.value = plan;
  }
  widget.dispatchEvent(new Event('change'));
}

class EroProductOptions extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[data-ero-swap]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    event.preventDefault();
    if (link.getAttribute('aria-current') === 'true') return;

    // Mark the choice right away so the click feels instant while the page loads.
    const list = link.closest('ul');
    for (const other of list?.querySelectorAll('a[aria-current]') || []) {
      other.removeAttribute('aria-current');
      other.classList.remove('is-current');
    }
    link.setAttribute('aria-current', 'true');
    link.classList.add('is-current');

    swapTo(link.href, link.dataset.eroSwap === 'product' ? 'push' : 'replace', link.dataset.eroKey);
  };
}

class EroPurchaseOptions extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  #onChange = () => this.#sync();

  #sync() {
    const choice = this.querySelector('.ero-purchase__radio:checked');
    const subscribe = choice instanceof HTMLInputElement && choice.value === 'subscribe';

    for (const option of this.querySelectorAll('.ero-purchase__option')) {
      if (!(option instanceof HTMLElement)) continue;
      option.classList.toggle('is-selected', option.dataset.option === (subscribe ? 'subscribe' : 'once'));
    }

    const plans = this.querySelector('.ero-purchase__plans');
    const sellingPlan = this.querySelector('.ero-purchase__selling-plan');
    if (!(sellingPlan instanceof HTMLInputElement)) return;

    if (plans instanceof HTMLSelectElement) sellingPlan.value = plans.value;
    sellingPlan.disabled = !subscribe;
  }
}

if (!customElements.get('ero-product-options')) {
  customElements.define('ero-product-options', EroProductOptions);

  // Back/forward between flavours: restore that product in place.
  window.addEventListener('popstate', () => {
    if (document.querySelector('ero-product-options')) swapTo(window.location.href, 'none');
  });
}

if (!customElements.get('ero-purchase-options')) {
  customElements.define('ero-purchase-options', EroPurchaseOptions);
}
